import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_ROLE_BASED_ACCESS_API,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a Role Profile row identifier.
 * @param {object} item Canvas Role Profile row
 * @return {*} identifier
 */
function rowId(item) {
	return item.rbacl_id ?? item.id;
}

/**
 * Normalize a Canvas Role Profile row.
 * @param {object} item Canvas Role Profile row
 * @return {object|null} agent candidate
 */
function normalizeRow(item) {
	const id = rowId(item);
	const name = item.role_name ?? item.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'role_based_access',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Resolve Role Profile candidates.
 *
 * A Role Profile is named by role_name, the same words a permission role
 * is named by. They are different things on different pages, which is why
 * this resource is role_based_access and the other is user_role.
 *
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveRoleBasedAccess(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_ROLE_BASED_ACCESS_API}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('role_based_access', rows, rowId);
		return {
			candidates: rows.map(normalizeRow).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('role_based_access', rows, rowId);
	return {candidates};
}

/**
 * Return a retained Role Profile row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedRoleBasedAccess(entityId) {
	return getResolvedEntity('role_based_access', entityId);
}
